export const BUYDOWN_BASE_LINE_TYPE = 'interest_rate_buydown';
export const BUYDOWN_AUTO_LINE_TYPE = 'interest_rate_buydown_auto';
export const BUYDOWN_DISPLAY_DESCRIPTION = 'Temporary Subsidy Buydown';

const MONEY_TOLERANCE = 0.01;
const NUMERIC_SUFFIX_RE = /_\d+$/;
const BUYDOWN_LABEL_RE = /\b(adjusted\s+temporary\s+subsidy|temporary\s+subsidy|buydown\s+fund\s+amount|interest\s+rate\s+buydown|temporary\s+buydown|buydown\s+subsidy|buydown)\b/i;
const FUNDING_DEDUCTION_STRING_KEYS = ['fundingDeductionList', 'fundingFeeList', 'fundingFees'];
const FUNDING_DEDUCTION_ARRAY_KEYS = ['fundingDeductions', 'fundingDeductionList', 'fundingFees'];

const DESCRIPTION_KEYS = [
  'description',
  'name',
  'lineDescription',
  'label',
  'feeDescription',
  'feeName',
  'line1320',
  'NEWHUD2.X4638'
];

const TOTAL_AMOUNT_KEYS = [
  'totalAmount',
  'totalFeeAmount',
  'totalPaidByAmount',
  'totalPaidAmount',
  'closingDisclosureAmount',
  'loanEstimateAmount',
  'amount',
  'paidAmount',
  'NEWHUD2.X4577'
];

const PAYER_COMPONENT_AMOUNT_KEYS = [
  'borPaidAmount',
  'borrPaidAmount',
  'borrowerPaidAmount',
  'borrowerAmount',
  'borrowerFinancedAmount',
  'borrowerPTCAmount',
  'borrowerPACAmount',
  'borrowerPOCAmount',
  'borrowerTotalPaidAmount',
  'borrowerTotal',
  'sellerAmount',
  'sellerPaidAmount',
  'selPaidAmount',
  'sellerPACAmount',
  'sellerPOCAmount',
  'sellerTotal',
  'lenderAmount',
  'lenderPaidAmount',
  'lenderPACAmount',
  'lenderPOCAmount',
  'lenderTotal',
  'otherAmount',
  'otherPaidAmount',
  'otherPACAmount',
  'otherPOCAmount',
  'otherTotal',
  'NEWHUD2.X4640',
  'NEWHUD2.X4641'
];

type InvestorCostLike = {
  id: string;
  description: string;
  amount: number;
};

type CostsStateLike<TInvestor extends InvestorCostLike = InvestorCostLike> = {
  investor: TInvestor[];
};

type LineTypeLike = {
  category?: string | null;
  line_type?: string | null;
  description?: string | null;
};

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function getArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    : [];
}

function getRecordList(rawData: Record<string, unknown> | null, key: string): Record<string, unknown>[] {
  return getArray(rawData?.[key]);
}

function numericSuffixBase(lineType: string): string {
  return lineType.replace(NUMERIC_SUFFIX_RE, '');
}

function textFromKeys(record: Record<string, unknown>, keys: string[]): string {
  return keys
    .map(key => normalizeText(record[key]))
    .filter(Boolean)
    .join(' ');
}

function positiveNumberFromRecord(record: Record<string, unknown>, key: string): number {
  return Math.max(0, toFiniteNumber(record[key]));
}

function firstPositiveFromKeys(record: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const amount = positiveNumberFromRecord(record, key);
    if (amount > MONEY_TOLERANCE) {
      return amount;
    }
  }
  return 0;
}

function sumPositiveFromKeys(record: Record<string, unknown>, keys: string[]): number {
  return keys.reduce((sum, key) => sum + positiveNumberFromRecord(record, key), 0);
}

function collapseDelimitedAmounts(amounts: number[]): number {
  const positives = Array.from(new Set(
    amounts
      .map(amount => roundCents(Math.max(0, amount)))
      .filter(amount => amount > MONEY_TOLERANCE)
  ));
  if (positives.length === 0) {
    return 0;
  }
  if (positives.length === 1) {
    return positives[0];
  }

  for (const amount of positives) {
    const otherSum = positives.reduce((sum, other) => other === amount ? sum : sum + other, 0);
    if (Math.abs(amount - otherSum) <= MONEY_TOLERANCE) {
      return amount;
    }
  }
  return positives.reduce((sum, amount) => sum + amount, 0);
}

function isDelimitedRowIdentifier(value: string): boolean {
  const normalized = value.toLowerCase().replace(/\s+/g, '').replace(/^line/, '');
  return /^13\d{2}[a-z]?\.?$/.test(normalized)
    || /^\d{3,4}[a-z]\.?$/.test(normalized)
    || /^\d{3,4}\.$/.test(normalized);
}

export function isBuydownLabel(value: unknown): boolean {
  return BUYDOWN_LABEL_RE.test(normalizeText(value));
}

export function isBuydownLineType(lineType: unknown, description?: unknown): boolean {
  const normalized = normalizeText(lineType).toLowerCase();
  const base = numericSuffixBase(normalized);
  return normalized === BUYDOWN_AUTO_LINE_TYPE
    || base === BUYDOWN_BASE_LINE_TYPE
    || isBuydownLabel(description);
}

export function canonicalInvestorCostBaseType(lineType: unknown, description?: unknown): string {
  const normalized = normalizeText(lineType);
  if (isBuydownLineType(normalized, description)) {
    return BUYDOWN_BASE_LINE_TYPE;
  }
  return numericSuffixBase(normalized);
}

export function normalizeExtractedInvestorCostType(type: unknown, label?: unknown): string {
  return isBuydownLineType(type, label) ? BUYDOWN_BASE_LINE_TYPE : normalizeText(type);
}

export function isBuydownPassThrough(item: LineTypeLike): boolean {
  return item.category === 'investor_cost' && isBuydownLineType(item.line_type, item.description);
}

export function normalizeExtractedFieldsForBuydown<T extends { investorCosts?: unknown }>(fields: T): T {
  const investorCosts = Array.isArray(fields.investorCosts) ? fields.investorCosts : null;
  if (!investorCosts) {
    return fields;
  }

  let changed = false;
  const normalizedCosts = investorCosts.map((entry) => {
    const record = getRecord(entry);
    if (!record) {
      return entry;
    }

    const normalizedType = normalizeExtractedInvestorCostType(record.type, record.label);
    if (normalizedType !== record.type) {
      changed = true;
      return { ...record, type: normalizedType };
    }
    return entry;
  });

  return changed ? { ...fields, investorCosts: normalizedCosts } : fields;
}

function getLoanRawData(loanLike: unknown): Record<string, unknown> | null {
  const loan = getRecord(loanLike);
  if (!loan) {
    return null;
  }
  return getRecord(loan.raw_data) ?? loan;
}

function sumStructuredBuydowns(rawData: Record<string, unknown> | null): number {
  const loanProductData = getRecord(rawData?.loanProductData);
  const buydowns = getArray(loanProductData?.buydowns);
  return buydowns.reduce((sum, buydown) => sum + Math.max(0, toFiniteNumber(buydown.fundTotalAmount)), 0);
}

function getRemainingBuydown(rawData: Record<string, unknown> | null): number {
  const loanProductData = getRecord(rawData?.loanProductData);
  return Math.max(0, toFiniteNumber(loanProductData?.remainingBuydownAmount));
}

function explicitBuydownAmount(row: Record<string, unknown>): number {
  const totalAmount = firstPositiveFromKeys(row, TOTAL_AMOUNT_KEYS);
  if (totalAmount > MONEY_TOLERANCE) {
    return totalAmount;
  }
  return sumPositiveFromKeys(row, PAYER_COMPONENT_AMOUNT_KEYS);
}

// A bare feeType is never trusted as a buydown. Legacy GFE/MLDS line 819
// (UserDefinedFee_819, classic field 81) can carry stale scratch amounts on
// TRID-era LE/CD loans where no GFE exists, so only a buydown-labeled
// description qualifies a fee row.
function isExplicitBuydownFee(row: Record<string, unknown>): boolean {
  const description = textFromKeys(row, DESCRIPTION_KEYS);
  return isBuydownLabel(description);
}

function sumExplicitBuydownFees(rawData: Record<string, unknown> | null): number {
  const fees = [
    ...getRecordList(rawData, 'fees'),
    ...getRecordList(rawData, 'fundingFees'),
    ...getRecordList(rawData, 'fundingDeductions')
  ];
  return fees.reduce((sum, fee) => {
    if (!isExplicitBuydownFee(fee)) {
      return sum;
    }
    return sum + explicitBuydownAmount(fee);
  }, 0);
}

function rowAmountFromDelimitedText(row: string): number {
  const amounts = row
    .split('|')
    .map(token => {
      const trimmed = token.trim();
      if (isDelimitedRowIdentifier(trimmed) || /%/.test(trimmed)) {
        return 0;
      }
      const amount = toFiniteNumber(trimmed);
      return amount >= 1 ? amount : 0;
    });
  return collapseDelimitedAmounts(amounts);
}

function sumBuydownRowsFromDelimitedText(value: unknown): number {
  if (typeof value !== 'string') {
    return 0;
  }
  return value
    .split('^')
    .reduce((sum, row) => {
      if (!isBuydownLabel(row)) {
        return sum;
      }
      return sum + rowAmountFromDelimitedText(row);
    }, 0);
}

function sumFundingDeductionFallbacks(rawData: Record<string, unknown> | null): number {
  const funding = getRecord(rawData?.funding);
  const sources = [rawData, funding].filter((record): record is Record<string, unknown> => !!record);

  const stringTotal = sources.reduce((sum, source) =>
    sum + FUNDING_DEDUCTION_STRING_KEYS
      .reduce((innerSum, key) => innerSum + sumBuydownRowsFromDelimitedText(source[key]), 0), 0);

  const arrayTotal = sources
    .flatMap(source => FUNDING_DEDUCTION_ARRAY_KEYS.flatMap(key => getRecordList(source, key)))
    .reduce((sum, row) => isExplicitBuydownFee(row) ? sum + explicitBuydownAmount(row) : sum, 0);

  return stringTotal + arrayTotal;
}

function sumLine1320Fields(rawData: Record<string, unknown> | null): number {
  const candidates = [
    rawData,
    getRecord(rawData?.fields),
    getRecord(rawData?.gfe),
    getRecord(rawData?.feeDetails)
  ].filter((record): record is Record<string, unknown> => !!record);

  return candidates.reduce((sum, record) => {
    const description = textFromKeys(record, DESCRIPTION_KEYS);
    if (!isBuydownLabel(description)) {
      return sum;
    }
    return sum + explicitBuydownAmount(record);
  }, 0);
}

function sumExplicitBuydownLeFees(loanLike: unknown): number {
  const loan = getRecord(loanLike);
  const leFees = getArray(loan?.le_fees);
  return leFees.reduce((sum, fee) => {
    if (!isExplicitBuydownFee(fee)) {
      return sum;
    }
    return sum + explicitBuydownAmount(fee);
  }, 0);
}

export function extractBuydownPassThroughAmount(loanLike: unknown): number {
  const rawData = getLoanRawData(loanLike);
  const structuredTotal = sumStructuredBuydowns(rawData);
  if (structuredTotal > MONEY_TOLERANCE) {
    return roundCents(structuredTotal);
  }

  const remainingBuydown = getRemainingBuydown(rawData);
  if (remainingBuydown > MONEY_TOLERANCE) {
    return roundCents(remainingBuydown);
  }

  const explicitRawFees = sumExplicitBuydownFees(rawData);
  if (explicitRawFees > MONEY_TOLERANCE) {
    return roundCents(explicitRawFees);
  }

  const fundingDeductionFallback = sumFundingDeductionFallbacks(rawData);
  if (fundingDeductionFallback > MONEY_TOLERANCE) {
    return roundCents(fundingDeductionFallback);
  }

  const line1320Fallback = sumLine1320Fields(rawData);
  if (line1320Fallback > MONEY_TOLERANCE) {
    return roundCents(line1320Fallback);
  }

  const explicitLeFees = sumExplicitBuydownLeFees(loanLike);
  return explicitLeFees > MONEY_TOLERANCE ? roundCents(explicitLeFees) : 0;
}

export function normalizeBuydownPassThroughCostsForDraft<TCosts extends CostsStateLike>(
  costs: TCosts,
  sourceAmount?: number | null
): TCosts {
  const authoritativeAmount = Math.max(0, toFiniteNumber(sourceAmount));
  const buydownRows = costs.investor.filter(item => isBuydownLineType(item.id, item.description));
  const existingAmount = buydownRows.find(item => Math.abs(toFiniteNumber(item.amount)) > MONEY_TOLERANCE)?.amount ?? 0;
  const amount = authoritativeAmount > MONEY_TOLERANCE ? roundCents(authoritativeAmount) : roundCents(existingAmount);

  if (amount <= MONEY_TOLERANCE && buydownRows.length === 0) {
    return costs;
  }

  const otherInvestorCosts = costs.investor.filter(item => !isBuydownLineType(item.id, item.description));
  const canonicalRow = {
    id: BUYDOWN_AUTO_LINE_TYPE,
    description: BUYDOWN_DISPLAY_DESCRIPTION,
    amount
  } as TCosts['investor'][number];
  const nextInvestor = amount > MONEY_TOLERANCE
    ? [...otherInvestorCosts, canonicalRow]
    : otherInvestorCosts;

  const unchanged = nextInvestor.length === costs.investor.length
    && nextInvestor.every((item, index) => {
      const current = costs.investor[index];
      return current.id === item.id
        && current.description === item.description
        && toFiniteNumber(current.amount) === toFiniteNumber(item.amount);
    });

  return unchanged ? costs : { ...costs, investor: nextInvestor };
}
